import { useEffect, useState } from "react";
import axios from "axios";
import { Users, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only referrals leaderboard. Shows total referrals + top inviters.
 * Rendered on AdminDashboard alongside the other insight panels.
 */
export const ReferralsInsightPanel = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, leaderboard: [] });
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/referrals`);
        if (!cancelled) setData(res.data || { total: 0, leaderboard: [] });
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load referrals.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100"
      data-testid="referrals-insight-panel"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#EAF0F9] flex items-center justify-center">
          <Users className="w-5 h-5 text-[#3090A8]" />
        </div>
        <div>
          <h2 className="font-outfit font-medium text-lg text-[#1B2349]">
            Referrals
          </h2>
          <p className="text-sm text-[#718096]">
            Members who invited a friend that then signed up.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#718096] py-6">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading referrals...
        </div>
      ) : err ? (
        <p className="text-sm text-[#D98880]" data-testid="referrals-error">
          {typeof err === "string" ? err : "Failed to load referrals."}
        </p>
      ) : (
        <>
          <div className="mb-4">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E7F0F4] text-[#3090A8] text-sm font-medium"
              data-testid="referrals-total"
            >
              {data.total} total referral{data.total === 1 ? "" : "s"}
            </span>
          </div>
          {data.leaderboard.length === 0 ? (
            <p className="text-sm text-[#718096]">
              No referrals yet. Once members start sharing, top inviters will show up here.
            </p>
          ) : (
            <div className="space-y-2">
              {data.leaderboard.slice(0, 10).map((row) => (
                <div
                  key={row.referrer_user_id}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[#FBF8F1] border border-slate-100"
                  data-testid={`referral-row-${row.referrer_user_id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#1B2349] truncate">
                      {row.name || row.email || row.referrer_user_id}
                    </div>
                    <div className="text-xs text-[#718096] truncate">
                      {row.email || row.referrer_user_id}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-sm font-semibold text-[#3090A8]">
                    {row.count}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReferralsInsightPanel;
